'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';
import { cn } from './utils';

type TooltipSide = 'top' | 'right' | 'bottom' | 'left';
type TooltipAlign = 'start' | 'center' | 'end';

const TooltipProviderContext = React.createContext({
  delayDuration: 80,
  skipDelayDuration: 60,
});

type TooltipTriggerChildProps = {
  onMouseEnter?: (event: React.MouseEvent) => void;
  onMouseLeave?: (event: React.MouseEvent) => void;
  onFocus?: (event: React.FocusEvent) => void;
  onBlur?: (event: React.FocusEvent) => void;
};

const TooltipContext = React.createContext<{
  open: boolean;
  setOpen: (_open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  openWithDelay: () => void;
  closeWithDelay: () => void;
} | null>(null);

function TooltipProvider({
  children,
  delayDuration = 80,
  skipDelayDuration = 60,
}: {
  children: React.ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
}) {
  return (
    <TooltipProviderContext.Provider value={{ delayDuration, skipDelayDuration }}>
      {children}
    </TooltipProviderContext.Provider>
  );
}

function Tooltip({ children, onOpenChange }: { children: React.ReactNode; onOpenChange?: (open: boolean) => void }) {
  const [open, setOpen] = React.useState<boolean>(false);
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const { delayDuration, skipDelayDuration } = React.useContext(TooltipProviderContext);
  const openTimerRef = React.useRef<number | null>(null);
  const closeTimerRef = React.useRef<number | null>(null);

  const clearTimers = React.useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const setOpenWithCallback = React.useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange],
  );

  const openWithDelay = React.useCallback(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (delayDuration <= 0) {
      setOpenWithCallback(true);
      return;
    }

    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
    }
    openTimerRef.current = window.setTimeout(() => {
      setOpenWithCallback(true);
      openTimerRef.current = null;
    }, delayDuration);
  }, [delayDuration, setOpenWithCallback]);

  const closeWithDelay = React.useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }

    if (skipDelayDuration <= 0) {
      setOpenWithCallback(false);
      return;
    }

    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
    }
    closeTimerRef.current = window.setTimeout(() => {
      setOpenWithCallback(false);
      closeTimerRef.current = null;
    }, skipDelayDuration);
  }, [setOpenWithCallback, skipDelayDuration]);

  React.useEffect(() => clearTimers, [clearTimers]);

  return (
    <TooltipContext.Provider
      value={{
        open,
        setOpen: setOpenWithCallback,
        triggerRef,
        openWithDelay,
        closeWithDelay,
      }}
    >
      {children}
    </TooltipContext.Provider>
  );
}

function TooltipTrigger({
  children,
  asChild,
  ...props
}: React.HTMLAttributes<HTMLElement> & {
  children: React.ReactNode;
  asChild?: boolean;
}) {
  const tooltip = React.useContext(TooltipContext);
  if (!tooltip) {
    return <>{children}</>;
  }
  const { openWithDelay, closeWithDelay, setOpen, triggerRef } = tooltip;

  const attachRef = (node: HTMLElement | null) => {
    triggerRef.current = node;
  };

  const setChildRef = React.useCallback((childRef: React.Ref<HTMLElement> | undefined, node: HTMLElement | null) => {
    if (!childRef) return;
    if (typeof childRef === 'function') {
      childRef(node);
      return;
    }
    if (typeof childRef === 'object' && 'current' in childRef) {
      (childRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  }, []);

  if (asChild && React.isValidElement(children)) {
    const child = children as React.ReactElement<TooltipTriggerChildProps>;
    const childRef = (child as React.ReactElement & { ref?: React.Ref<HTMLElement> }).ref;
    const mergedRef = (node: HTMLElement | null) => {
      attachRef(node);
      setChildRef(childRef, node);
    };

    return React.cloneElement(child, {
      ...props,
      ref: mergedRef,
      onMouseEnter: (event: React.MouseEvent) => {
        child.props.onMouseEnter?.(event);
        openWithDelay();
      },
      onMouseLeave: (event: React.MouseEvent) => {
        child.props.onMouseLeave?.(event);
        closeWithDelay();
      },
      onFocus: (event: React.FocusEvent) => {
        child.props.onFocus?.(event);
        setOpen(true);
      },
      onBlur: (event: React.FocusEvent) => {
        child.props.onBlur?.(event);
        setOpen(false);
      },
    } as TooltipTriggerChildProps);
  }

  return (
    <span
      ref={attachRef}
      onMouseEnter={openWithDelay}
      onMouseLeave={closeWithDelay}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
      {...props}
    >
      {children}
    </span>
  );
}

function TooltipContent({
  className,
  side = 'top',
  align = 'center',
  sideOffset = 8,
  children,
  style,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  side?: TooltipSide;
  align?: TooltipAlign;
  sideOffset?: number;
  children: React.ReactNode;
  style?: React.CSSProperties;
}) {
  const EXIT_ANIMATION_MS = 110;
  const tooltip = React.useContext(TooltipContext);
  const [mounted, setMounted] = React.useState(false);
  const [isPresent, setIsPresent] = React.useState(false);
  const [position, setPosition] = React.useState<{ top: number; left: number; transform: string; side: TooltipSide }>({
    top: 0,
    left: 0,
    transform: 'translate(-50%, -100%)',
    side: 'top',
  });
  const contentRef = React.useRef<HTMLDivElement>(null);

  const getAnchorRect = React.useCallback((triggerEl: HTMLElement): DOMRect => {
    const anchorPadding = Number(triggerEl.dataset.tooltipAnchorPadding ?? '');
    if (Number.isFinite(anchorPadding) && anchorPadding > 0) {
      const rect = triggerEl.getBoundingClientRect();
      return DOMRect.fromRect({
        x: rect.left - anchorPadding,
        y: rect.top - anchorPadding,
        width: rect.width + anchorPadding * 2,
        height: rect.height + anchorPadding * 2,
      });
    }

    // Include overflow descendants (selection/glow rings) in the anchor bounds.
    // This preserves "visual anchor" semantics instead of only the trigger box.
    const baseRect = triggerEl.getBoundingClientRect();
    let minTop = baseRect.top;
    let minLeft = baseRect.left;
    let maxRight = baseRect.right;
    let maxBottom = baseRect.bottom;

    const descendants = triggerEl.querySelectorAll<HTMLElement>('*');
    descendants.forEach(node => {
      const rect = node.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        return;
      }
      minTop = Math.min(minTop, rect.top);
      minLeft = Math.min(minLeft, rect.left);
      maxRight = Math.max(maxRight, rect.right);
      maxBottom = Math.max(maxBottom, rect.bottom);
    });

    return DOMRect.fromRect({
      x: minLeft,
      y: minTop,
      width: Math.max(0, maxRight - minLeft),
      height: Math.max(0, maxBottom - minTop),
    });
  }, []);

  const getTransform = React.useCallback((nextSide: TooltipSide, nextAlign: TooltipAlign) => {
    if (nextSide === 'top') {
      if (nextAlign === 'start') return 'translate(0, -100%)';
      if (nextAlign === 'end') return 'translate(-100%, -100%)';
      return 'translate(-50%, -100%)';
    }
    if (nextSide === 'bottom') {
      if (nextAlign === 'start') return 'translate(0, 0)';
      if (nextAlign === 'end') return 'translate(-100%, 0)';
      return 'translate(-50%, 0)';
    }
    if (nextSide === 'left') {
      if (nextAlign === 'start') return 'translate(-100%, 0)';
      if (nextAlign === 'end') return 'translate(-100%, -100%)';
      return 'translate(-100%, -50%)';
    }
    if (nextAlign === 'start') return 'translate(0, 0)';
    if (nextAlign === 'end') return 'translate(0, -100%)';
    return 'translate(0, -50%)';
  }, []);

  React.useEffect(() => setMounted(true), []);

  if (!tooltip) {
    return null;
  }

  const { open, triggerRef } = tooltip;

  React.useEffect(() => {
    if (open) {
      setIsPresent(true);
      return;
    }
    const timer = window.setTimeout(() => {
      setIsPresent(false);
    }, EXIT_ANIMATION_MS);
    return () => window.clearTimeout(timer);
  }, [open]);

  const updatePosition = React.useCallback(() => {
    if (!open || !triggerRef.current || !contentRef.current) {
      return;
    }
    const triggerRect = getAnchorRect(triggerRef.current);
    const contentRect = contentRef.current.getBoundingClientRect();
    const viewportPadding = 8;

    let top = triggerRect.top;
    let left = triggerRect.left;
    let transform = getTransform(side, align);
    let resolvedSide: TooltipSide = side;

    if (side === 'top') {
      top = triggerRect.top - sideOffset;
      if (align === 'start') {
        left = triggerRect.left;
      } else if (align === 'end') {
        left = triggerRect.right;
      } else {
        left = triggerRect.left + triggerRect.width / 2;
      }
    } else if (side === 'bottom') {
      top = triggerRect.bottom + sideOffset;
      if (align === 'start') {
        left = triggerRect.left;
      } else if (align === 'end') {
        left = triggerRect.right;
      } else {
        left = triggerRect.left + triggerRect.width / 2;
      }
    } else if (side === 'left') {
      left = triggerRect.left - sideOffset;
      if (align === 'start') {
        top = triggerRect.top;
      } else if (align === 'end') {
        top = triggerRect.bottom;
      } else {
        top = triggerRect.top + triggerRect.height / 2;
      }
    } else {
      left = triggerRect.right + sideOffset;
      if (align === 'start') {
        top = triggerRect.top;
      } else if (align === 'end') {
        top = triggerRect.bottom;
      } else {
        top = triggerRect.top + triggerRect.height / 2;
      }
    }

    // Clamp to viewport to avoid clipping.
    if (side === 'top' || side === 'bottom') {
      if (left - contentRect.width / 2 < viewportPadding) {
        left = viewportPadding;
        transform = transform.replace('-50%', '0');
      } else if (left + contentRect.width / 2 > window.innerWidth - viewportPadding) {
        left = window.innerWidth - viewportPadding;
        transform = transform.replace('-50%', '-100%');
      }
      if (side === 'top' && top - contentRect.height < viewportPadding) {
        resolvedSide = 'bottom';
        top = triggerRect.bottom + sideOffset;
        transform = getTransform(resolvedSide, align);
      } else if (side === 'bottom' && top + contentRect.height > window.innerHeight - viewportPadding) {
        resolvedSide = 'top';
        top = triggerRect.top - sideOffset;
        transform = getTransform(resolvedSide, align);
      }
    } else {
      if (top - contentRect.height / 2 < viewportPadding) {
        top = viewportPadding;
        transform = transform.replace('-50%', '0');
      } else if (top + contentRect.height / 2 > window.innerHeight - viewportPadding) {
        top = window.innerHeight - viewportPadding;
        transform = transform.replace('-50%', '-100%');
      }
      if (side === 'left' && left - contentRect.width < viewportPadding) {
        resolvedSide = 'right';
        left = triggerRect.right + sideOffset;
        transform = getTransform(resolvedSide, align);
      } else if (side === 'right' && left + contentRect.width > window.innerWidth - viewportPadding) {
        resolvedSide = 'left';
        left = triggerRect.left - sideOffset;
        transform = getTransform(resolvedSide, align);
      }
    }

    setPosition({ top, left, transform, side: resolvedSide });
  }, [align, getAnchorRect, getTransform, open, side, sideOffset, triggerRef]);

  React.useLayoutEffect(() => {
    if (!open) {
      return;
    }
    updatePosition();
  }, [open, children, updatePosition]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onChange = () => updatePosition();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);

    const triggerObserver = new ResizeObserver(onChange);
    const contentObserver = new ResizeObserver(onChange);
    if (triggerRef.current) {
      triggerObserver.observe(triggerRef.current);
    }
    if (contentRef.current) {
      contentObserver.observe(contentRef.current);
    }

    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
      triggerObserver.disconnect();
      contentObserver.disconnect();
    };
  }, [open, triggerRef, updatePosition]);

  if (!isPresent) {
    return null;
  }

  if (!mounted) {
    return null;
  }

  const transitionStyle = {
    transform: `${position.transform} scale(${open ? 1 : 0.96})`,
    opacity: open ? 1 : 0,
    transition: 'opacity 110ms ease-out, transform 120ms cubic-bezier(0.16, 1, 0.3, 1)',
    willChange: 'opacity, transform',
  };

  const content = (
    <div
      data-slot="tooltip-content-wrapper"
      className="tw-fixed tw-z-[10000] tw-pointer-events-none"
      style={{
        top: position.top,
        left: position.left,
        ...transitionStyle,
      }}
      {...props}
    >
      <span
        data-slot="tooltip-arrow"
        aria-hidden="true"
        className={cn(
          'tw-absolute tw-size-2.5 tw-rotate-45 tw-z-0',
          position.side === 'top' && 'tw-bottom-0 tw-left-1/2 -tw-translate-x-1/2 tw-translate-y-[58%]',
          position.side === 'bottom' && 'tw-top-0 tw-left-1/2 -tw-translate-x-1/2 -tw-translate-y-[58%]',
          position.side === 'left' && 'tw-right-0 tw-top-1/2 tw-translate-x-[58%] -tw-translate-y-1/2',
          position.side === 'right' && 'tw-left-0 tw-top-1/2 -tw-translate-x-[58%] -tw-translate-y-1/2',
        )}
        style={{
          background: 'var(--popover, #393939)',
        }}
      />
      <div
        ref={contentRef}
        data-side={position.side}
        className={cn(
          'tw-relative tw-z-10 tw-w-fit tw-max-w-[min(90vw,28rem)] tw-rounded-lg tw-px-3 tw-py-2 tw-text-xs tw-leading-snug',
          className,
        )}
        style={{
          background: 'var(--popover, #393939)',
          color: 'var(--popover-foreground, #ffffff)',
          boxShadow: 'var(--elevation-lg)',
          ...style,
        }}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
